import { produce } from 'immer';
import { createSelector } from 'reselect';
import * as api from '../../lib/api';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';

const AsyncType = ScopedAsyncType('CORNRX');
const Type = ScopedType('CORNRX');

const SLICE = 'cornrx';
const LOAD_GROWER_CORNRX = AsyncType('LOAD_GROWER_CORNRX');
const LOAD_AAID_CORNRX = AsyncType('LOAD_AAID_CORNRX');
const LOAD_AAID_COEFFICIENT = AsyncType('LOAD_AAID_COEFFICIENT');
const SET_AAID_COEFFICIENT = Type('SET_AAID_COEFFICIENT');
const UPDATE_CORNRX = Type('UPDATE_CORNRX');
const SET_ROW_SPACING = Type('SET_ROW_SPACING');

const initialState = {
  prescriptions: {},
  coefficient: null,
  planterSpacing: '30',
};

export const selectors = {
  cornRXAAIDS: (state) => state[SLICE].cornRXAAIDS,
  mapLayers: (state) => state[SLICE].mapLayers,
  prescriptions: (state) => state[SLICE].prescriptions,
  planterSpacing: (state) => state[SLICE].planterSpacing,
  coefficient: (state) => state[SLICE].coefficient,
  isLoading: (state) => !state[SLICE].cornRXAAIDS,
  cornrxFields: createSelector(
    (state) => state[SLICE].cornRXAAIDS || [],
    (state) => state[SLICE].planterSpacing,
    (cornRXAAIDS, planterSpacing) => (fields) =>
      fields.map((field) => {
        const tmpField = { HasCornRX: 'No', Min: 0, Max: 0, Avg: 0, ...field };
        const rates = cornRXAAIDS.filter(
          (yf) =>
            yf.AAID.replace(/{/g, '').replace(/}/g, '').toUpperCase() ===
            field.aaManagedId,
        );

        if (rates.length) {
          tmpField.HasCornRX = 'Yes';
          tmpField.status = rates[0].rxstatus;
          tmpField.rx = rates[0].rx;
          tmpField.soildate = rates[0].soildate;
          tmpField.started = rates[0].started;
          tmpField.statusdetails = rates[0].statusdetails;
          tmpField.Min = rates[0][`minrate${planterSpacing}`];
          tmpField.Max = rates[0][`maxrate${planterSpacing}`];
          tmpField.Avg = rates[0][`avgrate${planterSpacing}`];
          tmpField.TotalSeeds = rates[0][`totalseeds${planterSpacing}`];
          tmpField.UnitsPerField = rates[0][`unitsperfield${planterSpacing}`];
        }

        return tmpField;
      }),
  ),
};

function loadGrowerHasCornRX(growerId) {
  return asyncAction(LOAD_GROWER_CORNRX, () =>
    api.getGrowerHasCornRX(growerId),
  );
}

function loadCornRXData(aaid) {
  return asyncAction(LOAD_AAID_CORNRX, () => api.getAAIDCornRX(aaid));
}

function updateCornRXData(yldData) {
  return {
    type: UPDATE_CORNRX,
    payload: yldData,
  };
}

function loadCoefficient(aaid, growerId) {
  return asyncAction(LOAD_AAID_COEFFICIENT, () =>
    api.getCoefficent(aaid, growerId),
  );
}

function setCoefficient(coefficient) {
  return {
    type: SET_AAID_COEFFICIENT,
    payload: coefficient,
  };
}

function setPlanterSpacing(spacing) {
  return {
    type: SET_ROW_SPACING,
    payload: spacing,
  };
}

export const actions = {
  loadGrowerHasCornRX,
  loadCornRXData,
  updateCornRXData,
  loadCoefficient,
  setCoefficient,
  setPlanterSpacing,
};

function reduceLoadAAIDCornRXStart(state) {
  state.prescriptions = {};
}

function reduceLoadAAIDCornRXComplete(state, payload) {
  state.prescriptions = payload;
}

function reduceLoadAAIDCornRXFailure(state) {
  state.prescriptions = [];
}

function reduceLoadGrowerHasCornRXStart(state) {
  delete state.cornRXAAIDS;
}

function reduceLoadGrowerHasCornRXComplete(state, payload) {
  state.cornRXAAIDS = payload;
}

function reduceLoadGrowerHasCornRXFailure(state) {
  state.cornRXAAIDS = [];
}

function reduceLoadAAIDCoefficientStart(state) {
  state.coefficient = null;
}

function reduceLoadAAIDCoefficientComplete(state, payload) {
  state.coefficient = payload;
}

function reduceLoadAAIDCoefficientFailure(state) {
  state.coefficient = null;
}

function reduceSetAAIDCoefficient(state, payload) {
  state.coefficient = payload;
}

function reduceUpdateCornRXData(state, payload) {
  state.prescriptions = payload;
}

function reduceSetPlanterSpacing(state, payload) {
  state.planterSpacing = payload;
}

const reducerHandlers = {
  [LOAD_AAID_CORNRX.start]: reduceLoadAAIDCornRXStart,
  [LOAD_AAID_CORNRX.complete]: reduceLoadAAIDCornRXComplete,
  [LOAD_AAID_CORNRX.failure]: reduceLoadAAIDCornRXFailure,
  [LOAD_GROWER_CORNRX.start]: reduceLoadGrowerHasCornRXStart,
  [LOAD_GROWER_CORNRX.complete]: reduceLoadGrowerHasCornRXComplete,
  [LOAD_GROWER_CORNRX.failure]: reduceLoadGrowerHasCornRXFailure,
  [LOAD_AAID_COEFFICIENT.start]: reduceLoadAAIDCoefficientStart,
  [LOAD_AAID_COEFFICIENT.complete]: reduceLoadAAIDCoefficientComplete,
  [LOAD_AAID_COEFFICIENT.failure]: reduceLoadAAIDCoefficientFailure,
  [SET_AAID_COEFFICIENT]: reduceSetAAIDCoefficient,
  [UPDATE_CORNRX]: reduceUpdateCornRXData,
  [SET_ROW_SPACING]: reduceSetPlanterSpacing,
};

export const reducers = {
  [SLICE]: (state = initialState, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
